import { useNavigate } from 'react-router-dom';
import { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  GraduationCap, BookOpen, Plane, Award, Target, Heart, Home, Users
} from 'lucide-react';
import { usePlatformFlags } from '@/hooks/usePlatformFlags';

const NAV_ITEMS = [
  { id: 'all', label: 'All', icon: null, path: '/explore', flagKey: null },
  { id: 'colleges', label: 'Colleges', icon: GraduationCap, path: '/colleges', flagKey: 'colleges' },
  { id: 'courses', label: 'Courses', icon: BookOpen, path: '/colleges?tab=courses', flagKey: 'courses' },
  { id: 'visa', label: 'Visa', icon: Plane, path: '/visa', flagKey: 'visa' },
  { id: 'rpl', label: 'RPL', icon: Award, path: '/colleges?tab=rpl', flagKey: 'rpl' },
  { id: 'skills', label: 'Skills', icon: Target, path: '/skill-assessment', flagKey: 'skills' },
  { id: 'insurance', label: 'Insurance', icon: Heart, path: '/insurance', flagKey: 'insurance' },
  { id: 'rentals', label: 'Rentals', icon: Home, path: '/rentals', flagKey: 'rentals' },
  { id: 'agents', label: 'Agents', icon: Users, path: '/agents', flagKey: 'agents' },
];

export default function SectionNavBar({ active }) {
  const navigate = useNavigate();
  const { isEnabled } = usePlatformFlags();
  const scrollRef = useRef(null);
  const [counts, setCounts] = useState({});
  const [countsLoaded, setCountsLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/explore/feed`, { params: { page: 1, limit: 6 } })
      .then(res => { setCounts(res.data?.service_counts || {}); setCountsLoaded(true); })
      .catch(() => setCountsLoaded(true));
  }, []);

  // Auto-scroll to active pill
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const activeBtn = el.querySelector(`[data-testid="nav-${active}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }, [active, counts]);

  const visibleItems = NAV_ITEMS.filter(item => !item.flagKey || isEnabled(item.flagKey));
  const allTotal = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div className="py-2">
      <div
        ref={scrollRef}
        className="flex items-center gap-2 overflow-x-auto pb-1 flex-nowrap scrollbar-hide -mx-4 px-4"
        data-testid="section-nav-bar"
      >
        {visibleItems.map(item => {
          const isActive = active === item.id;
          const count = item.id === 'all' ? allTotal : (counts[item.id] || 0);
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.id}`}
              className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all active:scale-95 ${
                isActive
                  ? 'bg-slate-900 text-white'
                  : 'bg-white border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {Icon && <Icon className="h-3 w-3" />}
              {item.label}{countsLoaded ? ` (${count.toLocaleString()})` : ''}
            </button>
          );
        })}
      </div>
      <style>{`.scrollbar-hide::-webkit-scrollbar { display: none; } .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
    </div>
  );
}
